(() => {
  'use strict';
  if (customElements.get('mas-search')) return;
  const cache = new Map();

  class MasSearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.input = this.querySelector('input[name="q"]');
      this.panel = this.querySelector('.mas-quick-search__panel');
      this.clearButton = this.querySelector('.mas-quick-search__clear');
      this.status = this.querySelector('[data-search-status]');
      this.revision = 0;
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.clearButton.hidden = true;
        this.dismiss();
        this.panel.replaceChildren();
        this.input.focus();
      });
      this.input.addEventListener('compositionstart', () => { this.composing = true; this.dismiss(); });
      this.input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); });
      this.input.addEventListener('input', () => {
        this.clearButton.hidden = !this.input.value;
        if (!this.composing) this.schedule();
      });
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim() && this.panel.hidden) this.schedule();
      });
      this.addEventListener('keydown', event => this.onKeydown(event));
      this.addEventListener('focusout', event => {
        // Touch browsers can move focus to the dialog while dismissing the
        // keyboard, before the result's click. Keep that original link in the
        // DOM until activation completes; otherwise the click hits the dialog.
        if (!this.selectingResult && event.relatedTarget && !this.contains(event.relatedTarget)) this.dismiss();
      });
      this.querySelector('form').addEventListener('submit', () => this.dismiss());
    }

    disconnectedCallback() { this.dismiss(); }

    dismiss() {
      this.selectingResult = false;
      clearTimeout(this.timer);
      this.controller?.abort();
      this.revision++;
      this.panel.hidden = true;
      this.panel.removeAttribute('aria-busy');
      if (this.hasAttribute('data-predictive-enabled')) this.input.setAttribute('aria-expanded', 'false');
      this.status.textContent = '';
    }

    schedule() {
      this.dismiss();
      const query = this.input.value.trim();
      if (!query || !this.hasAttribute('data-predictive-enabled')) return;
      this.timer = setTimeout(() => this.suggest(query), 220);
    }

    async suggest(query) {
      const revision = this.revision;
      const url = new URL(this.dataset.predictiveUrl, location.origin);
      url.search = new URLSearchParams({
        q: query,
        'resources[type]': 'query,product,collection',
        'resources[limit]': '3',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'show',
        section_id: 'mas-predictive-search'
      });
      const key = url.href;
      this.controller = new AbortController();
      this.status.textContent = 'Searching…';
      this.panel.setAttribute('aria-busy', 'true');
      try {
        let html = cache.get(key);
        if (!html) {
          const response = await fetch(url.href, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Search unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = doc.querySelector('[data-mas-predictive-content]');
          if (!content) throw new Error('Search unavailable');
          html = content.outerHTML;
          cache.set(key, html);
          if (cache.size > 20) cache.delete(cache.keys().next().value);
        }
        if (revision !== this.revision || !this.contains(document.activeElement)) return;
        this.panel.innerHTML = html;
        const count = this.panel.querySelectorAll('.mas-predictive__product').length;
        this.status.textContent = count ? `${count} product suggestions. Use the down arrow to explore.` : 'No product suggestions. Press Enter for all results.';
        this.open();
      } catch (error) {
        if (error.name === 'AbortError' || revision !== this.revision || !this.contains(document.activeElement)) return;
        const message = document.createElement('p');
        message.className = 'mas-predictive__empty';
        message.textContent = 'Suggestions are unavailable. Press Enter or use the search button to see results.';
        this.panel.replaceChildren(message);
        this.status.textContent = message.textContent;
        this.open();
      } finally {
        if (revision === this.revision) this.panel.removeAttribute('aria-busy');
      }
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    onKeydown(event) {
      this.selectingResult = false;
      if (event.isComposing) return;
      if (event.key === 'Escape' && !this.panel.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.input.focus();
        this.dismiss();
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.panel.hidden) return;
      const links = [...this.panel.querySelectorAll('a[href]')];
      if (!links.length) return;
      event.preventDefault();
      const index = links.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') links[(index + 1) % links.length].focus();
      else if (index <= 0) this.input.focus();
      else links[index - 1].focus();
    }
  }
  customElements.define('mas-search', MasSearch);

  document.addEventListener('pointerdown', event => {
    document.querySelectorAll('mas-search').forEach(search => {
      search.selectingResult = !search.panel.hidden && search.panel.contains(event.target);
      if (!search.contains(event.target)) search.dismiss();
    });
  });
  // Do not release on pointerup: focusout/mousedown can occur between it and
  // click on touch devices. Cancellation still leaves native scrolling intact.
  ['click', 'pointercancel'].forEach(type => {
    document.addEventListener(type, () => {
      document.querySelectorAll('mas-search').forEach(search => { search.selectingResult = false; });
    });
  });

  function initSearchDialog() {
    const dialog = document.getElementById('MasSearchDialog');
    if (!dialog || dialog.dataset.ready) return;
    dialog.dataset.ready = 'true';
    let opener;
    let backdropPointerDown = false;
    const isOutsideDialog = event => {
      const rect = dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right ||
        event.clientY < rect.top || event.clientY > rect.bottom;
    };
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-mas-search-open]');
      if (!trigger || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      opener = trigger;
      dialog.showModal();
      document.documentElement.classList.add('mas-search-dialog-open');
      dialog.querySelector('input[name="q"]').focus();
    }, true);
    dialog.querySelector('[data-search-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('pointerdown', event => {
      backdropPointerDown = event.target === dialog && isOutsideDialog(event);
    });
    dialog.addEventListener('pointercancel', () => { backdropPointerDown = false; });
    dialog.addEventListener('click', event => {
      // Padding and clicks retargeted after keyboard/layout changes are not
      // backdrop clicks. Require both the start and end to be outside the box.
      const close = backdropPointerDown && event.target === dialog && isOutsideDialog(event);
      backdropPointerDown = false;
      if (close) dialog.close();
    });
    dialog.addEventListener('close', () => {
      backdropPointerDown = false;
      dialog.querySelector('mas-search').dismiss();
      document.documentElement.classList.remove('mas-search-dialog-open');
      opener?.focus();
    });
  }

  function initResults() {
    const root = document.querySelector('[data-mas-search-results]');
    if (!root) return;
    const url = new URL(location.href);
    // Legacy links without a type include editorial content. Scope the entire
    // Shopify query, rather than filtering one paginated response in the browser.
    if (root.hasAttribute('data-mixed-search')) {
      url.searchParams.set('type', 'product');
      url.searchParams.delete('page');
    }
    // Older search links omit this option and Shopify defaults to sold-out last.
    // Keep the whole server-paginated result set ordered by relevance instead.
    if (url.searchParams.has('q') && (url.searchParams.get('type') || 'product').split(',').includes('product')) {
      url.searchParams.set('options[unavailable_products]', 'show');
    }
    if (url.href !== location.href) location.replace(url.href);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { initSearchDialog(); initResults(); });
  else { initSearchDialog(); initResults(); }
})();
